import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { createContext, useEffect, useCallback, useRef, useState } from "react";
import { BASE_URL } from './api';
import { toSafeMediaUrl } from "./utils/mediaUrl";
import { apiFetch, HttpError, sendPresenceHeartbeat, setUnauthorizedHandler } from "@shared/api";
import { readWebSessionCache, saveWebSessionCache, clearWebSessionCache } from './utils/webSessionCache';
import { Box, Typography } from '@mui/material';
import { TavelButton } from './components/base/Button';
const PRESENCE_HEARTBEAT_INTERVAL_MS = 2 * 60 * 1000;
export const AuthContext = createContext({
    isAuthenticated: false,
    isAdmin: false,
    emailConfirmed: false,
    socialEmailVerified: false,
    isSocialAccount: false,
    socialProviders: [],
    userId: null,
    userEmail: null,
    profileImage: null,
    updateProfileImage: () => { },
    isLoading: true,
    refresh: async () => { },
    logout: () => { },
});
const getUserId = (data) => {
    if (typeof data?.user === "string")
        return data.user;
    if (typeof data?.user?.id === "string")
        return data.user.id;
    if (typeof data?.user_id === "string")
        return data.user_id;
    return null;
};
export const isTechnicalAccountEmail = (email) => {
    const normalizedEmail = typeof email === "string" ? email.trim() : "";
    return /^vk_[^@\s]+@vk\.local$/i.test(normalizedEmail);
};
const getDisplayableEmail = (email) => {
    if (typeof email !== "string")
        return null;
    const normalizedEmail = email.trim();
    if (!normalizedEmail || isTechnicalAccountEmail(normalizedEmail))
        return null;
    return normalizedEmail;
};
const getSocialProviders = (data) => {
    if (!Array.isArray(data?.social_providers))
        return [];
    return data.social_providers.filter((provider) => typeof provider === "string");
};
export function AuthProvider({ children }) {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [userId, setUserId] = useState(null);
    const [userEmail, setUserEmail] = useState(null);
    const [profileImage, setProfileImage] = useState(null);
    const [emailConfirmed, setEmailConfirmed] = useState(false);
    const [socialEmailVerified, setSocialEmailVerified] = useState(false);
    const [isSocialAccount, setIsSocialAccount] = useState(false);
    const [socialProviders, setSocialProviders] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [startupError, setStartupError] = useState(false);
    const heartbeatAbortRef = useRef(null);
    const sessionRevision = useRef(0);
    const acceptedToken = useRef(null);
    const pendingSession = useRef(null);
    const clearUser = useCallback(() => {
        acceptedToken.current = null;
        setIsAuthenticated(false);
        setIsAdmin(false);
        setEmailConfirmed(false);
        setSocialEmailVerified(false);
        setIsSocialAccount(false);
        setSocialProviders([]);
        setUserId(null);
        setUserEmail(null);
        setProfileImage(null);
    }, []);
    const acceptUser = useCallback((data, token) => {
        acceptedToken.current = token;
        setIsAuthenticated(true);
        setIsAdmin(!!data.is_admin);
        setEmailConfirmed(!!data.email_confirmed);
        setUserId(getUserId(data));
        setSocialEmailVerified(!!data.social_email_verified);
        setIsSocialAccount(!!data.is_social_account);
        setSocialProviders(getSocialProviders(data));
        setUserEmail(getDisplayableEmail(data.profile?.email ?? data.email ?? data.user?.email ?? data.user_email));
        const image = data.profile?.profile_image ?? data.profile_image ?? data.user?.profile_image;
        setProfileImage(typeof image === "string" ? toSafeMediaUrl(image) : null);
    }, []);
    const refresh = useCallback(async () => {
        const token = localStorage.getItem("accessToken");
        if (pendingSession.current?.token === token)
            return pendingSession.current.request;
        const revision = ++sessionRevision.current;
        const current = () => sessionRevision.current === revision && localStorage.getItem("accessToken") === token;
        setStartupError(false);
        setIsLoading(true);
        const request = (async () => {
            try {
                if (!token) {
                    clearWebSessionCache();
                    clearUser();
                    return;
                }
                if (acceptedToken.current !== token)
                    clearUser();
                const cached = readWebSessionCache(token);
                if (cached) {
                    acceptUser(cached, token);
                    setIsLoading(false);
                }
                const data = await apiFetch(`/user/api/user/me/`, {
                    baseUrl: BASE_URL, token, method: "GET", redirectOnUnauthorized: false,
                    notifyOnUnauthorized: false, requestTimeoutMs: 15000,
                });
                if (!current())
                    return;
                if (data?.is_authenticated === true) {
                    acceptUser(data, token);
                    saveWebSessionCache(token, data);
                }
                else {
                    localStorage.removeItem("accessToken");
                    clearWebSessionCache();
                    clearUser();
                }
            }
            catch (error) {
                if (!current())
                    return;
                if (error instanceof HttpError && error.status === 401) {
                    localStorage.removeItem("accessToken");
                    clearWebSessionCache();
                    clearUser();
                }
                else if (token && acceptedToken.current !== token) {
                    setStartupError(true);
                }
                // Network errors, timeouts and 5xx preserve the confirmed session.
            }
            finally {
                if (sessionRevision.current === revision)
                    setIsLoading(false);
            }
        })();
        pendingSession.current = { token, request };
        try {
            await request;
        }
        finally {
            if (pendingSession.current?.request === request)
                pendingSession.current = null;
        }
    }, [acceptUser, clearUser]);
    useEffect(() => setUnauthorizedHandler(async (failedToken) => {
        if (localStorage.getItem("accessToken") === failedToken)
            await refresh();
    }), [refresh]);
    useEffect(() => {
        refresh();
    }, [refresh]);
    const heartbeat = useCallback(async () => {
        if (!isAuthenticated || document.visibilityState !== "visible" || !navigator.onLine)
            return;
        const token = localStorage.getItem("accessToken");
        if (!token || heartbeatAbortRef.current)
            return;
        const controller = new AbortController();
        heartbeatAbortRef.current = controller;
        try {
            await sendPresenceHeartbeat({ baseUrl: BASE_URL, token, signal: controller.signal });
        }
        catch (error) {
            if (error?.name !== "AbortError") {
                // Presence must never interrupt the user's work if the network is unstable.
            }
        }
        finally {
            if (heartbeatAbortRef.current === controller)
                heartbeatAbortRef.current = null;
        }
    }, [isAuthenticated]);
    useEffect(() => {
        if (!isAuthenticated)
            return undefined;
        const sendHeartbeat = () => void heartbeat();
        sendHeartbeat();
        const intervalId = window.setInterval(sendHeartbeat, PRESENCE_HEARTBEAT_INTERVAL_MS);
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible")
                sendHeartbeat();
        };
        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("online", sendHeartbeat);
        return () => {
            window.clearInterval(intervalId);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            window.removeEventListener("online", sendHeartbeat);
            heartbeatAbortRef.current?.abort();
            heartbeatAbortRef.current = null;
        };
    }, [heartbeat, isAuthenticated]);
    const logout = () => {
        setStartupError(false);
        sessionRevision.current += 1;
        pendingSession.current = null;
        acceptedToken.current = null;
        clearWebSessionCache();
        setIsLoading(false);
        setIsAuthenticated(false);
        setIsAdmin(false);
        setEmailConfirmed(false);
        setSocialEmailVerified(false);
        setIsSocialAccount(false);
        setSocialProviders([]);
        setUserId(null);
        setUserEmail(null);
        setProfileImage(null);
        localStorage.removeItem("accessToken");
    };
    const updateProfileImage = useCallback((nextProfileImage) => {
        setProfileImage(typeof nextProfileImage === "string" ? toSafeMediaUrl(nextProfileImage) : null);
    }, []);
    return (_jsx(AuthContext.Provider, { value: {
            isAuthenticated,
            isAdmin,
            emailConfirmed,
            socialEmailVerified,
            isSocialAccount,
            socialProviders,
            userId,
            userEmail,
            profileImage,
            updateProfileImage,
            isLoading,
            refresh,
            logout,
        }, children: startupError ? _jsx(Box, { role: "alert", sx: { minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3, bgcolor: 'var(--back-color)' }, children: _jsxs(Box, { sx: { maxWidth: 420, p: 3, borderRadius: '16px', bgcolor: 'var(--background-color-menu)', color: 'var(--braun-color)' }, children: [_jsx(Typography, { sx: { mb: 2 }, children: "\u041D\u0435 \u0443\u0434\u0430\u043B\u043E\u0441\u044C \u043F\u0440\u043E\u0432\u0435\u0440\u0438\u0442\u044C \u0432\u0445\u043E\u0434. \u041F\u0440\u043E\u0432\u0435\u0440\u044C\u0442\u0435 \u0438\u043D\u0442\u0435\u0440\u043D\u0435\u0442 \u0438 \u043F\u043E\u0432\u0442\u043E\u0440\u0438\u0442\u0435 \u043F\u043E\u043F\u044B\u0442\u043A\u0443." }), _jsxs(Box, { sx: { display: 'flex', gap: 1, flexWrap: 'wrap' }, children: [_jsx(TavelButton, { text: "\u041F\u043E\u0432\u0442\u043E\u0440\u0438\u0442\u044C", onClick: () => void refresh(), bgColor: "var(--green-color)", textColor: "var(--white-color)", hoverColor: "var(--button-green-hover)", size: "small" }), _jsx(TavelButton, { text: "\u0412\u044B\u0439\u0442\u0438", onClick: logout, size: "small" })] })] }) }) : children }));
}
